import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { useParentDashboard } from '../hooks/api'
import { DashboardCard } from '../components/DashboardCard'
import { TabNavigation } from '../components/TabNavigation'
import { PageHeader } from '../components/PageHeader'
import { getErrorMessage } from '../utils/errorHandler'

// Mock data for fallback (will be removed once API is working)
const mockDashboardData = {
  student: {
    name: 'น้องใส',
    nickname: 'ใส', 
    session_group: 'main',
    next_class: '2025-08-24T09:00:00.000Z'
  },
  payment_status: {
    current_month: {
      status: 'unpaid' as const, // Changed to unpaid to test widget display
      amount: 1600,
      due_date: '2025-08-31'
    },
    next_month: {
      status: 'unpaid' as const,
      amount: 1600,
      due_date: '2025-09-30'
    }
  },
  pending_expenses: {
    total: 155,
    count: 3,
    items: [
      {
        expense_date: '2025-08-17',
        description: 'ข้าวกล่อง + น้ำ',
        amount: 55,
        category: 'ค่าอาหาร'
      },
      {
        expense_date: '2025-08-16', 
        description: 'ขนม + โค้ก',
        amount: 50,
        category: 'ค่าขนม'
      },
      {
        expense_date: '2025-08-15',
        description: 'ดินสอ + ยางลบ',
        amount: 50,
        category: 'อุปกรณ์การเรียน'
      }
    ]
  },
  latest_lesson: {
    date: '2025-08-17',
    subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
    homework_assigned: true
  },
  attendance: {
    today_status: 'present' as const, // 'present' | 'absent' | 'late' | 'not_marked'
    this_week_attendance: [
      { date: '2025-08-17', status: 'present' }, // Saturday
      { date: '2025-08-18', status: 'present' }  // Sunday
    ],
    monthly_stats: {
      total_sessions: 8,
      present_sessions: 7,
      late_sessions: 1,
      absent_sessions: 0
    }
  },
  announcements: [
    {
      title: 'เปิดเทอมใหม่',
      priority: 'important' as const,
      published_at: '2025-08-17T14:30:00.000Z',
      content: 'เปิดเทอมวันที่ 1 กันยายน 2568 เวลา 09:00 น.'
    }
  ]
}

export const ParentDashboard: React.FC = () => {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  
  // Fetch dashboard data from API
  const { 
    data: dashboardData, 
    isLoading, 
    error, 
    refetch 
  } = useParentDashboard()
  
  // Use API data if available, otherwise fallback to mock data
  // Ensure all required properties exist with fallbacks
  const data = dashboardData ? {
    student: dashboardData.student || mockDashboardData.student,
    payment_status: dashboardData.payment_status || mockDashboardData.payment_status,
    pending_expenses: dashboardData.pending_expenses || mockDashboardData.pending_expenses,
    latest_lesson: dashboardData.latest_lesson || mockDashboardData.latest_lesson,
    attendance: dashboardData.attendance || mockDashboardData.attendance,
    announcements: dashboardData.announcements || mockDashboardData.announcements
  } : mockDashboardData
  
  // Utility functions
  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }
  
  const formatThaiBaht = (amount: number) => {
    return `฿${new Intl.NumberFormat('th-TH').format(amount)}`
  }
  
  // Check if we should show payment widget
  const shouldShowPaymentWidget = () => {
    if (!data?.payment_status?.current_month) {
      return false
    }
    
    const today = new Date()
    const dayOfMonth = today.getDate()
    const currentMonthStatus = data.payment_status.current_month.status
    
    // Show only in first 2 weeks (day 1-14) AND if not yet paid
    return dayOfMonth <= 14 && currentMonthStatus !== 'paid'
  }
  
  const getAttendanceStatusIcon = (status: string) => {
    switch (status) {
      case 'present':
        return '✅'
      case 'late':
        return '⏰'
      case 'absent':
        return '❌'
      default:
        return '⭕'
    }
  }
  
  const getAttendanceStatusText = (status: string) => {
    switch (status) {
      case 'present':
        return 'มาเรียน'
      case 'late':
        return 'มาสาย'
      case 'absent':
        return 'ขาดเรียน'
      default:
        return 'ยังไม่เช็ค'
    }
  }
  
  const getAttendanceRate = () => {
    if (!data?.attendance?.monthly_stats) {
      return 0
    }
    
    const { total_sessions, present_sessions, late_sessions } = data.attendance.monthly_stats
    
    if (total_sessions === 0) {
      return 0
    }
    
    return Math.round(((present_sessions + late_sessions) / total_sessions) * 100)
  }
  
  // Handle loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader 
          title="หน้าหลัก"
          subtitle={`สวัสดี ${user?.display_name} 👋`}
        />
        <div className="px-4 py-6">
          <div className="animate-pulse space-y-6">
            <div className="bg-gray-200 h-32 rounded-lg"></div>
            <div className="bg-gray-200 h-24 rounded-lg"></div>
            <div className="bg-gray-200 h-40 rounded-lg"></div>
          </div>
        </div>
      </div>
    )
  }

  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader 
          title="หน้าหลัก"
          subtitle={`สวัสดี ${user?.display_name} 👋`}
        />
        <div className="px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800 font-medium">เกิดข้อผิดพลาด</div>
            <div className="text-red-600 text-sm mt-1">{getErrorMessage(error)}</div>
            <button
              onClick={() => refetch()}
              className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700"
            >
              ลองใหม่อีกครั้ง
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Page Header */}
      <PageHeader 
        title="หน้าหลัก"
        subtitle={`สวัสดี ${user?.display_name} 👋`}
        actions={
          <button
            onClick={logout}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ออกจากระบบ
          </button>
        }
      />
      
      {/* Main Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Payment Status Card - Only show in first 2 weeks if unpaid */}
        {shouldShowPaymentWidget() && (
          <DashboardCard
            title="💳 ค่าเรียนพิเศษ"
            className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200"
          >
            <div className="grid grid-cols-1 gap-4">
              {/* Current Month Only */}
              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <p className="text-sm text-gray-600">เดือนนี้ (ส.ค. 68)</p>
                  <p className="text-lg font-semibold">{formatThaiBaht(data?.payment_status?.current_month?.amount || 0)}</p>
                  <p className="text-xs text-gray-500">ครบกำหนด: {formatThaiDate(data?.payment_status?.current_month?.due_date || new Date().toISOString())}</p>
                </div>
                <div className="status-unpaid">
                  รอชำระ
                </div>
              </div>
              
              <button className="btn-payment w-full mt-2">
                ชำระค่าเรียนพิเศษ
              </button>
            </div>
          </DashboardCard>
        )}
        
        {/* Enrollment Management */}
        <DashboardCard
          title="📚 การลงทะเบียนเรียน"
          className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200"
        >
          <div className="space-y-4">
            {/* Quick Actions */}
            <div className="grid grid-cols-1 gap-3">
              <button 
                onClick={() => navigate('/courses')}
                className="flex items-center justify-between p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">ลงทะเบียนคอร์สใหม่</div>
                    <div className="text-sm text-gray-600">เลือกคอร์สเรียนสำหรับเดือนหน้า</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              <button 
                onClick={() => navigate('/enrollment/renew')}
                className="flex items-center justify-between p-3 bg-white rounded-lg border hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-gray-900">ต่ออายุคอร์สเรียน</div>
                    <div className="text-sm text-gray-600">ต่ออายุจากคอร์สที่เรียนอยู่</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Current Enrollment Status */}
            {data.student && (
              <div className="p-3 bg-white rounded-lg border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">สถานะปัจจุบัน</span>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                    กำลังเรียน
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>นักเรียน: <span className="font-medium">{data.student.name}</span></div>
                  <div>คอร์ส: <span className="font-medium">คอร์สเสาร์หลัก</span></div>
                  <div>เดือนที่ลงทะเบียน: <span className="font-medium">สิงหาคม 2568</span></div>
                </div>
              </div>
            )}
          </div>
        </DashboardCard>
        
        {/* Pending Expenses */}
        {data.pending_expenses.total > 0 && (
          <DashboardCard
            title="🧾 ค่าใช้จ่ายรอชำระ"
            className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold">
                  รวม {formatThaiBaht(data.pending_expenses.total)}
                </span>
                <span className="text-sm text-gray-600">
                  {data.pending_expenses.count} รายการ
                </span>
              </div>
              
              <div className="space-y-2">
                {data.pending_expenses.items.slice(0, 2).map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm p-2 bg-white rounded border">
                    <div>
                      <p className="font-medium">{item.description}</p>
                      <p className="text-gray-500 text-xs">{formatThaiDate(item.expense_date)}</p>
                    </div>
                    <span className="font-semibold">{formatThaiBaht(item.amount)}</span>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={() => navigate('/expenses')}
                className="btn-secondary w-full text-sm"
              >
                ดูทั้งหมด ({data.pending_expenses.count} รายการ)
              </button>
            </div>
          </DashboardCard>
        )}
        
        {/* Latest Lesson */}
        <DashboardCard
          title="📚 บทเรียนล่าสุด"
          className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200"
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {formatThaiDate(data.latest_lesson.date)}
              </span>
              {data.latest_lesson.homework_assigned && (
                <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                  มีการบ้าน
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {data.latest_lesson.subjects.map((subject, index) => (
                <div key={index} className="text-center p-2 bg-white rounded border">
                  <div className="text-sm font-medium text-gray-700">
                    {subject}
                  </div>
                </div>
              ))}
            </div>
            
            <button 
              onClick={() => navigate('/lessons')}
              className="btn-secondary w-full text-sm"
            >
              ดูรายละเอียดบทเรียน
            </button>
          </div>
        </DashboardCard>
        
        {/* Attendance Status */}
        <DashboardCard
          title="📋 สถานะการเข้าเรียน"
          className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200"
        >
          <div className="space-y-4">
            {/* Today's Status */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
              <div>
                <p className="text-sm text-gray-600">วันนี้</p>
                <p className="text-lg font-semibold flex items-center space-x-2">
                  <span>{getAttendanceStatusIcon(data.attendance.today_status)}</span>
                  <span>{getAttendanceStatusText(data.attendance.today_status)}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">อัตราเข้าเรียนเดือนนี้</p>
                <p className="text-xl font-bold text-blue-600">{getAttendanceRate()}%</p>
                <p className="text-xs text-gray-500">{data.attendance.monthly_stats.present_sessions + data.attendance.monthly_stats.late_sessions}/{data.attendance.monthly_stats.total_sessions} ครั้ง</p>
              </div>
            </div>
            
            {/* This Week Overview */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">สัปดาห์นี้ (เสาร์-อาทิตย์)</p>
              <div className="grid grid-cols-2 gap-2">
                {data.attendance.this_week_attendance.map((session, index) => (
                  <div key={index} className="text-center p-3 bg-white rounded border">
                    <div className="text-xs text-gray-500 mb-1">
                      {new Date(session.date).toLocaleDateString('th-TH', { weekday: 'long' })}
                    </div>
                    <div className="text-sm text-gray-600 mb-1">
                      {formatThaiDate(session.date)}
                    </div>
                    <div className="text-lg">
                      {getAttendanceStatusIcon(session.status)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Monthly Stats */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">สถิติเดือนนี้ (8 ครั้ง)</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                  <div className="text-lg font-bold text-green-600">
                    {data.attendance.monthly_stats.present_sessions}
                  </div>
                  <div className="text-xs text-green-800">เข้าเรียน</div>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded border border-yellow-200">
                  <div className="text-lg font-bold text-yellow-600">
                    {data.attendance.monthly_stats.late_sessions}
                  </div>
                  <div className="text-xs text-yellow-800">เข้าสาย</div>
                </div>
                <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                  <div className="text-lg font-bold text-red-600">
                    {data.attendance.monthly_stats.absent_sessions}
                  </div>
                  <div className="text-xs text-red-800">ขาดเรียน</div>
                </div>
              </div>
            </div>
          </div>
        </DashboardCard>
        
        {/* Announcements */}
        {data.announcements.length > 0 && (
          <DashboardCard
            title="📢 ประกาศล่าสุด"
            className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
          >
            <div className="space-y-3">
              {data.announcements.map((announcement, index) => (
                <div key={index} className="p-3 bg-white rounded-lg border">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-gray-900">
                      {announcement.title}
                    </h4>
                    {announcement.priority === 'important' && (
                      <span className="text-xs bg-red-500 text-white px-2 py-1 rounded-full">
                        สำคัญ
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 mb-2">
                    {announcement.content}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatThaiDate(announcement.published_at)}
                  </p>
                </div>
              ))}
            </div>
          </DashboardCard>
        )}
      </div>
      
      {/* Bottom Navigation */}
      <TabNavigation />
    </div>
  )
}